import type { UseAddressFormValues } from '@montugroup/circuit-shared/hooks/user/useAddressForm';
import type { Address } from '@montugroup/circuit-shared/types/address';
import type { IUser } from '@montugroup/circuit-shared/types/user';
import type { AddressDataType } from '@montugroup/design-system';
import type { useForm } from 'react-hook-form';

export type FormMethods = ReturnType<typeof useForm<UseAddressFormValues>>;

export const splitAddress = (addressString: string): { line1: string; line2: string } => {
  const [line1 = '', line2 = ''] = addressString.split('$$$$') || [];
  return { line1, line2 };
};

/**
 * Populates the address form with user data if an address exists.
 */
export const populateAddressFormIfExists = (
  formMethods: FormMethods,
  userData: IUser | undefined,
  getUserState: () => string | null,
): void => {
  if (userData?.address) {
    const { line1, line2 } = splitAddress(userData.address);
    const state = getUserState();
    formMethods.setValue('addressLine1', String(line1 || ''));
    formMethods.setValue('addressLine2', String(line2 || ''));
    formMethods.setValue('suburb', String(userData.city || ''));
    formMethods.setValue('postcode', String(userData.zip_code || ''));
    formMethods.setValue('state', String(state || ''));
  }
};

export const formatAddressString = (address: Address): string => {
  let addressLine = address.addressLine1 || '';

  if (address.addressLine2) {
    addressLine = `${address.addressLine2}/${addressLine}`;
  }

  const addressParts = [addressLine, address.suburb, address.postcode, address.state].filter(Boolean);

  return addressParts.join(', ');
};

// omit geometry from Address comparison as it's not stable
type ComparableAddressKey = Exclude<keyof Address, 'geometry'>;

const ADDRESS_KEYS: readonly ComparableAddressKey[] = [
  'addressLine1',
  'addressLine2',
  'suburb',
  'state',
  'postcode',
] as const;

const norm = (v: unknown) => (v ?? '').toString().toLowerCase().replace(/\s+/g, ' ').trim();

export const addressesAreEqual = (a?: Partial<Address>, b?: Partial<Address>) => {
  if (a === b) {
    return true;
  }
  if (!a || !b) {
    return false;
  }

  return ADDRESS_KEYS.every((k) => norm(a[k]) === norm(b[k]));
};

export const formatPharmacyAddress = (address?: {
  streetNumberAndName?: string;
  suburb?: string;
  state?: string;
  postcode?: string;
}) =>
  address
    ? `${address.streetNumberAndName?.replace(/\${2,}/g, '').trim()}, ${address.suburb}, ${address.state} ${address.postcode}`
    : '';

export const createAddressFromData = (addressData: AddressDataType): Address => {
  const formattedAddress = [addressData.subpremise, addressData.street_number, addressData.address]
    .filter(Boolean)
    .join(' ');

  return {
    addressLine1: formattedAddress,
    addressLine2: '',
    state: addressData.state,
    suburb: addressData.suburb || addressData.city,
    postcode: addressData.postcode,
    ...(addressData?.geometry ? { geometry: addressData.geometry } : {}),
  };
};
